import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AlertController, ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs";
import { OrderModel } from "src/app/models/order.model";
import { IPayment } from "src/app/models/payment.model";
import { ProductModel } from "src/app/models/product.model";
import { DataService } from "src/app/services/data/data.service";
import { MathService } from "src/app/services/math/math.service";
import { OrderService } from "src/app/services/order/order.service";
import { PaymentService } from "src/app/services/payment/payment.service";
import { SaleService } from "src/app/services/sale/sale.service";
import { UtilsService } from "src/app/services/utils/utils.service";
import { CashInComponent } from "../cash-in/cash-in.component";

@Component({
  selector: "order",
  templateUrl: "./order.component.html",
  styleUrls: ["./order.component.scss"]
})
export class OrderComponent implements OnInit {
  @Input()
  mode: string; // edit, view, order, catalog, sale

  _order: OrderModel;
  @Input('order') 
  set order(value:OrderModel){
    // console.log('order',value);
    this._order = value;
    this.checkIfProductsNeeded();
  }

  _format:string = 'small-card';
  @Input('format') 
  set format(value:string){
    this._format = value;
    this.checkIfProductsNeeded();
  }

  isModalOpen = false;

  @Input()
  role: string;

  @Input()
  debug: string;

  @Output() statusChanged = new EventEmitter<OrderModel>();
  @Output() totalChanged = new EventEmitter<OrderModel>();

  constructor(public orderService:OrderService, public saleService:SaleService, private translateService:TranslateService,
    private modalCtrl:ModalController, private dataService:DataService, private alertCtrl:AlertController,
    private mathService:MathService, public paymentService:PaymentService, public utils:UtilsService) {}

  ngOnInit() {}

  checkIfProductsNeeded(){
    if(this._order && this._format && this._order.details && !this._order.products 
      && (this._format=='card' || this._format=='full')){
      // console.log('get ordered products');
      this.getProducts();
    }
  }

  getProducts(){
    // console.log(this._order);
    this.dataService.getProducerOrderProductsOrderedObj(this._order.tournee.producer.key, this._order.tournee.key,this._order.key
      ).pipe(take(1)).subscribe(references => {
        // console.log('get products', this._order.key);
        this._order.products = {references:references};
        this.refreshTotal(this._order);
      })
  }

  async requestPayment(item:OrderModel){
    const payment:IPayment = {
      debitedFunds:{
        currency: item.details.currency,
        amount: item.finances.totals.sold
      },
      cardType: 'CB_VISA_MASTERCARD'
    }
    if(item.market){
      payment.marketKey = item.market.key;
    }
    else{
      payment.tourneeKey = item.tournee.key;
    }
    this.paymentService.sendPaymentRequestByEmail(payment,item,this.order.tournee,'web');
  }

  async markOrderAccepted(order:OrderModel){
    await this.orderService.changeOrderStatus(order,order.tournee.producer,'accepted');
    this.statusChanged.emit(order);
  }

  async markOrderPrepared(order: OrderModel): Promise<void> {
    console.log(order);
    const oldStatus = order.status;
    let message = "";
    const inputs = [];
    if (order.finances.totals.amountStatus == "estimated" || order.details.productMode == "free") {
      message = this.translateService.instant("tournee.AmountMessage");
      inputs.push({
        type: "number",
        name: "amount",
        label: this.translateService.instant("tournee.AmountFinal"),
        value: order.finances.totals.sold,
        placeholder: this.translateService.instant("tournee.AmountPlaceholder")
      });
    }
    if(order.tournee.details.deliveryType =='vendingMachine' || order.tournee.details.deliveryType =='locker'){
      inputs.push({
        type: "text",
        name: "locker",
        placeholder: this.translateService.instant("order.LockerNumber"),
        label: this.translateService.instant("order.LockerNumber"),
      }, {
        type: "text",
        name: "secret",
        placeholder: this.translateService.instant("order.Secret"),
        label: this.translateService.instant("order.Secret"),
      } );
    }
    inputs.push({
      type: "textarea",
      name: "message",
      placeholder: this.translateService.instant("tournee.ClientMessage")
    });
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant("tournee.OrderReady?"),
      cssClass: "alertMsg",
      message: message,
      backdropDismiss: false,
      inputs: inputs,
      buttons: [
        { role: "cancel", text: this.translateService.instant("buttons.Cancel") },
        { role: "ok", text: this.translateService.instant("buttons.Validate") }
      ]
    });
    await alert.present();
    const alertResult = await alert.onDidDismiss();
    if (alertResult.role === "ok") {
      if ((alertResult.data.values.message && alertResult.data.values.message !== "") || (alertResult.data.values.secret && alertResult.data.values.secret !== "")) {
        order.details.commentPrepared = alertResult.data.values.message || null;
        order.details.secret = alertResult.data.values.secret || null;
        order.details.locker = alertResult.data.values.locker || null;
        await this.dataService.updateOrderPreparedCommentByProducer(order, order.tournee.producer);
      }
      if (order.finances.totals.amountStatus == "estimated"){
        if(alertResult.data.values.amount > 0 && alertResult.data.values.amount !== order.finances.totals.sold) {
          order.finances = {
            totals: {
              amountStatus: "setByProducer",
              calculated: order.finances.totals.sold,
              sold: parseFloat(alertResult.data.values.amount),
              shared: parseFloat(alertResult.data.values.amount),
            }
          }
        }
        else {
          order.finances.totals.amountStatus = "confirmedByProducer";
        }
        await this.dataService.updateOrderTotalSoldByProducer(order, order.tournee.producer);
      }
      await this.orderService.changeOrderStatus(order, order.tournee.producer, "prepared");
      this.statusChanged.emit(order);
    }
  }

  async markOrderRejected(order: OrderModel): Promise<void> {
    const oldStatus = order.status;
    const message = this.translateService.instant("tournee.RejectOrderReasonMessage");
    const inputs = [];
    inputs.push({
      type: "textarea",
      name: "message",
      placeholder: this.translateService.instant("tournee.RejectOrderReasonMessagePlaceholder")
    });
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant("tournee.RejectOrder"),
      cssClass: "alertMsg",
      message: message,
      backdropDismiss: false,
      inputs: inputs,
      buttons: [
        { role: "cancel", text: this.translateService.instant("buttons.Cancel") },
        { role: "ok", text: this.translateService.instant("buttons.Reject") }
      ]
    });
    await alert.present();
    const alertResult = await alert.onDidDismiss();
    if (alertResult.role === "ok") {
      if (alertResult.data.values.message && alertResult.data.values.message !== "") {
        order.details.commentRejected = alertResult.data.values.message;
        await this.dataService.updateOrderRejectedCommentByProducer(order, order.tournee.producer);
      }
      await this.orderService.changeOrderStatus(order, order.tournee.producer, "rejected");
    }
    this.statusChanged.emit(order);
  }

  async markOrderDelivered(order: OrderModel) {
    await this.orderService.changeOrderStatus(order, order.tournee.producer, "delivered");
    this.statusChanged.emit(order);
  }

  async markOrderNotDelivered(order:OrderModel){
    await this.orderService.changeOrderStatus(order,order.tournee.producer,'notDelivered');
    this.statusChanged.emit(order);
  }

  async cashIn(order:OrderModel){
    console.log(order);
    const modal = await this.modalCtrl.create({
      component: CashInComponent,
      presentingElement:await this.modalCtrl.getTop(),
      cssClass: 'small-modal',
      componentProps:{
        order:order,
        onValidate: async (payment:any) => {
          await this.addOrderPayment(order,payment);
          modal.dismiss();
        }
      }
    });
    return await modal.present();  
  }

  async delete(order:OrderModel){
    console.log(order);
    let message = this.translateService.instant('common.ConfirmDelete');
    const alert = await this.alertCtrl.create({
      header: this.translateService.instant('common.ConfirmDelete'),
      cssClass: "alertMsg",
      // message: message,
      backdropDismiss: false,
      buttons: [
        { role: "cancel", text: this.translateService.instant("buttons.Cancel") },
        { role: "ok", text: this.translateService.instant("buttons.Validate") }
      ]
    });
    await alert.present();
    const alertResult = await alert.onDidDismiss();
    if (alertResult.role === "ok") {
      await this.orderService.changeOrderStatus(order, order.tournee.producer, "cancelled");
      this.statusChanged.emit(order);
    }
  }

  async addOrderPayment(order:OrderModel, payment:IPayment){
    const paymentResult = await this.dataService.createOrderPayment(order,payment);
    console.log(order, payment, paymentResult);
    if(!order.payments){
      order.payments = {};
    }
    order.payments[paymentResult.key] = payment;
    await this.changeOrderPaymentStatus(order);
  }

  async changeOrderPaymentStatus(order: OrderModel): Promise<void> {
    const oldPaymentStatus = order.paymentStatus;
    this.refreshTotal(order);
    if(order.paymentStatus !== oldPaymentStatus){
      await this.paymentService.changeOrderPaymentStatus(order,order.tournee,order.tournee.producer,order.paymentStatus);
    }
  }

  refreshTotal(order:OrderModel){
    order = this.mathService.calculateOrderRevenue(order);
    this.totalChanged.emit(order);
  }

  onWillDismiss(event){
    this.isModalOpen = false;
  }

  async openOrder(order:OrderModel){
    // const modal = await this.modalCtrl.create({
    //   component: OrderComponent,
    //   cssClass: "modal",
    //   componentProps: {
    //     order: order,
    //     format:'full',
    //     role:'producer',
    //     mode:'edit',
    //     onCancel: () => {
    //       modal.dismiss();
    //     },
    //   }
    // });
    // return await modal.present();  
    console.log('open order', this._order);
    this.getProducts();
    this.isModalOpen = !this.isModalOpen;
  }

  async onQuantityChanged(product:ProductModel, order:OrderModel){
    order.products.references[product.key] = product;
    if(order.finances.totals.amountStatus == 'setByProducer' && order.finances.totals.calculated !== order.finances.totals.products){
      const message = this.translateService.instant("order.TotalSetByProducer.Message");
      const alert = await this.alertCtrl.create({
        header: this.translateService.instant("order.TotalSetByProducer.Title"),
        message: message,
        backdropDismiss: false,
        buttons: [
          { role: "ok", text: this.translateService.instant("order.TotalSetByProducer.Recompute") },
          { role: "cancel", text: this.translateService.instant("order.TotalSetByProducer.Keep") }
        ]
      });
      await alert.present();
      const alertResult = await alert.onDidDismiss();
      if (alertResult.role === "ok") {
        order.finances.totals.amountStatus = "confirmedByProducer"
      }
    }
    order = this.mathService.calculateOrderRevenue(order);
    await this.updateOrderProduct(order,product);
    this.totalChanged.emit(order);
  }

  updateOrderProduct(order:OrderModel,product:ProductModel){
    return this.dataService.updateProducerOrderProduct(order, product);
  }
}
